import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import type { TwapHistoryItem } from "#/hooks/use-hyperliquid-account";
import {
	formatCamelDashLabel,
	formatDetailedDateTime,
	formatPrice,
	formatSize,
	toNumber,
} from "../../lib/formatters";
import { numericString } from "./lib/sorting-fns";
import type { TwapTableMeta } from "./types/positions.types.ts";

const col = createColumnHelper<TwapHistoryItem>();

function formatElapsedTime(startTime: number, totalMinutes: number) {
	const elapsedMs = Math.max(
		0,
		Math.min(Date.now() - startTime, totalMinutes * 60_000),
	);
	const totalSeconds = Math.floor(elapsedMs / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	return [hours, minutes, seconds]
		.map((value) => value.toString().padStart(2, "0"))
		.join(":");
}

function getTwapStatusKey(
	status: TwapHistoryItem["status"]["status"],
): string | null {
	const map: Record<TwapHistoryItem["status"]["status"], string> = {
		activated: "twap.status.activated",
		terminated: "twap.status.terminated",
		finished: "twap.status.finished",
		error: "twap.status.error",
	};

	return map[status] ?? null;
}

export const twapActiveColumns = [
	col.accessor("state.coin", {
		id: "coin",
		header: () => <T keyName="twap.columns.coin" />,
		cell: ({ getValue }) => (
			<span className="text-label2-medium text-trade-text-strong">{getValue()}</span>
		),
	}),
	col.accessor("state.sz", {
		id: "size",
		header: () => <T keyName="twap.columns.size" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatSize(getValue())} {row.original.state.coin}
			</span>
		),
	}),
	col.accessor("state.executedSz", {
		id: "filled",
		header: () => <T keyName="twap.columns.filled" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => {
			const executedSize = Number(getValue());
			return (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{executedSize > 0
						? `${formatSize(getValue())} ${row.original.state.coin}`
						: "--"}
				</span>
			);
		},
	}),
	col.accessor(
		(row) => {
			const executedSz = toNumber(row.state.executedSz);
			const executedNtl = toNumber(row.state.executedNtl);
			return executedSz > 0 ? executedNtl / executedSz : 0;
		},
		{
			id: "avgPrice",
			header: () => <T keyName="twap.columns.avg-price" />,
			sortingFn: numericString,
			cell: ({ getValue }) => {
				const avgPrice = getValue() as number;
				return (
					<span className="text-label2-tabular-medium text-trade-text-secondary">
						{avgPrice > 0 ? formatPrice(avgPrice) : "--"}
					</span>
				);
			},
		},
	),
	col.accessor("state.minutes", {
		id: "executionTime",
		header: () => <T keyName="twap.columns.execution-time" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatElapsedTime(row.original.state.timestamp, getValue())} /{" "}
				<T keyName="twap.value.minutes" params={{ minutes: getValue() }} />
			</span>
		),
	}),
	col.accessor("state.reduceOnly", {
		id: "reduceOnly",
		header: () => <T keyName="twap.columns.reduce-only" />,
		cell: ({ getValue }) => (
			<span className="text-trade-text-secondary">
				{getValue() ? <T keyName="general.yes" /> : <T keyName="general.no" />}
			</span>
		),
	}),
	col.accessor("state.timestamp", {
		id: "createdTime",
		header: () => <T keyName="twap.columns.created-time" />,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-faint">
				{formatDetailedDateTime(getValue())}
			</span>
		),
	}),
	col.accessor("status", {
		id: "status",
		enableSorting: false,
		header: () => <T keyName="twap.columns.status" />,
		cell: ({ getValue, row, table }) => {
			const status = getValue().status;
			if (status === "activated") {
				const meta = table.options.meta as TwapTableMeta | undefined;
				const twapId = row.original.twapId;
				if (meta && twapId != null) {
					return (
						<button
							type="button"
							onClick={() => meta.onCancel(row.original.state.coin, twapId)}
							className="text-caption1-medium text-primary hover:text-primary/80 cursor-pointer"
						>
							<T keyName="twap.columns.status" />
						</button>
					);
				}
			}

			const statusKey = getTwapStatusKey(status);
			return (
				<span className="text-trade-text-strong">
					{statusKey ? (
						<T keyName={statusKey} />
					) : (
						formatCamelDashLabel(status)
					)}
				</span>
			);
		},
	}),
];

export const twapColumns = [
	col.accessor("state.timestamp", {
		id: "time",
		header: () => <T keyName="twap.columns.time" />,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-faint">
				{formatDetailedDateTime(getValue())}
			</span>
		),
	}),
	col.accessor("state.coin", {
		id: "coin",
		header: () => <T keyName="twap.columns.coin" />,
		cell: ({ getValue }) => (
			<span className="text-label2-medium text-trade-text-strong">{getValue()}</span>
		),
	}),
	col.accessor("state.sz", {
		id: "totalSize",
		header: () => <T keyName="twap.columns.total-size" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatSize(getValue())} {row.original.state.coin}
			</span>
		),
	}),
	col.accessor("state.executedSz", {
		id: "filled",
		header: () => <T keyName="twap.columns.filled" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => {
			const executedSize = Number(getValue());
			return (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{executedSize > 0
						? `${formatSize(getValue())} ${row.original.state.coin}`
						: "--"}
				</span>
			);
		},
	}),
	col.accessor("state.minutes", {
		id: "totalExecutionTime",
		header: () => <T keyName="twap.columns.total-execution-time" />,
		sortingFn: numericString,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{getValue()}m
			</span>
		),
	}),
	col.accessor("state.reduceOnly", {
		id: "reduceOnly",
		header: () => <T keyName="twap.columns.reduce-only" />,
		cell: ({ getValue }) => (
			<span className="text-trade-text-secondary">
				{getValue() ? <T keyName="general.yes" /> : <T keyName="general.no" />}
			</span>
		),
	}),
	col.accessor("state.randomize", {
		id: "randomize",
		header: () => <T keyName="twap.columns.randomize" />,
		cell: ({ getValue }) => (
			<span className="text-trade-text-secondary">
				{getValue() ? <T keyName="general.yes" /> : <T keyName="general.no" />}
			</span>
		),
	}),
	col.accessor("status", {
		id: "status",
		header: () => <T keyName="twap.columns.status" />,
		cell: ({ getValue }) => {
			const status = getValue().status;
			const statusKey = getTwapStatusKey(status);
			const className =
				status === "error"
					? "text-trade-red"
					: status === "finished"
						? "text-trade-green"
						: "text-trade-text-strong";
			return (
				<span className={className}>
					{statusKey ? (
						<T keyName={statusKey} />
					) : (
						formatCamelDashLabel(status)
					)}
				</span>
			);
		},
	}),
];
